import { FormEvent, useState } from 'react';
import type { MeResponse, SaleCard, SaleFindItem, SaleReturnResult } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney, formatQty, hasPermission } from '../format';
import { NoReceiptReturn } from '../components/NoReceiptReturn';
import { clampReturnQty, lineRefund, needsPin, refundSplit, remainingQty } from '../returns';

interface Props {
  me: MeResponse;
  onBack: () => void;
}

// Qaytarma: çek nömrəsi ilə satış tapılır, sətir üzrə qalan miqdar qaytarılır. Eyni UUID təkrar klikdə ikinci sənəd yaratmır.
export function Return({ me, onBack }: Props) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState<SaleFindItem[]>([]);
  const [card, setCard] = useState<SaleCard | null>(null);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [returnId, setReturnId] = useState(() => crypto.randomUUID());
  const [pin, setPin] = useState('');
  const [needPin, setNeedPin] = useState(false);
  const [done, setDone] = useState<SaleReturnResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı. Yenidən cəhd edin.');
      throw err;
    } finally {
      setBusy(false);
    }
  }

  const search = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      setFound(await api.saleFind(q.trim()));
    }).catch(() => undefined);
  };

  const pick = (id: string) => {
    run(async () => {
      const c = await api.saleCard(id);
      setCard(c);
      setQtys({});
      setFound([]);
      setDone(null);
      setNeedPin(false);
      setReturnId(crypto.randomUUID());
    }).catch(() => undefined);
  };

  const refund = card
    ? card.lines.reduce((s, l) => s + lineRefund(l, qtys[l.id] ?? 0), 0)
    : 0;
  const split = card ? refundSplit(card.total, { cash: card.cash, card: card.card, credit: card.credit }, Math.round(refund * 100) / 100) : null;
  const picked = card ? card.lines.filter((l) => (qtys[l.id] ?? 0) > 0) : [];

  const submit = () => {
    if (!card || picked.length === 0) return;
    const body = {
      id: returnId,
      lines: picked.map((l) => ({ docLineId: l.id, qty: qtys[l.id] })),
      pin: pin !== '' ? pin : null,
    };
    // PİN yalnız bu sorğu üçündür
    setPin('');
    run(async () => {
      try {
        const r = await api.saleReturn(card.id, body);
        setDone(r);
        setCard(null);
        setReturnId(crypto.randomUUID());
      } catch (err) {
        if (err instanceof ApiError && needsPin(err.code)) setNeedPin(true);
        throw err;
      }
    }).catch(() => undefined);
  };

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn back-home" onClick={onBack}>
          ← Ana səhifə
        </button>
        <span className="brand">Qaytarma</span>
      </header>
      <main className="page">
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {done && (
          <section className="card" aria-live="polite">
            <h2>Qaytarma yazıldı — {done.displayNo}</h2>
            <p>
              Qaytarılan {formatMoney(done.total)} · nağd {formatMoney(done.cash)} · kart {formatMoney(done.card)} · nisyə {formatMoney(done.credit)}
              {done.duplicate ? ' (bu sənəd artıq yazılmışdı)' : ''}
            </p>
          </section>
        )}

        {!card && (
          <section className="card">
            <form onSubmit={search} role="search" className="toolbar">
              <input
                type="search"
                className="search"
                placeholder="Çek nömrəsi (məs. SAT-2026-K01-000123)"
                aria-label="Çek nömrəsi"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button type="submit" className="btn-small" disabled={busy || q.trim().length < 3}>
                Tap
              </button>
            </form>
            {found.length > 0 && (
              <ul className="product-list">
                {found.map((f) => (
                  <li key={f.id}>
                    <button type="button" className="product-row" onClick={() => pick(f.id)}>
                      <span className="product-name mono">{f.displayNo}</span>
                      <span className="muted small">{formatMoney(f.total)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {card && (
          <>
            <section className="card">
              <h2>{card.displayNo}</h2>
              <p className="muted small">
                Cəm {formatMoney(card.total)} · naxd {formatMoney(card.cash)} · kart {formatMoney(card.card)} · nisyə {formatMoney(card.credit)}
              </p>
              <button type="button" className="link-btn" onClick={() => setCard(null)}>
                Başqa satış
              </button>
            </section>

            <section className="card">
              <h2>Sətirlər</h2>
              {card.lines.map((l) => {
                const rem = remainingQty(l);
                return (
                  <div className="purchase-line" key={l.id}>
                    <span className="product-name">{l.productName}</span>
                    <span className="muted small">
                      {formatQty(l.qty)} {l.unit} · qalan {formatQty(rem)}
                    </span>
                    <label>
                      Qaytarma miqdarı
                      <input
                        type="number"
                        min="0"
                        max={rem}
                        step="0.001"
                        disabled={rem <= 0}
                        value={qtys[l.id] ?? ''}
                        onChange={(e) => setQtys((cur) => ({ ...cur, [l.id]: clampReturnQty(Number(e.target.value), rem) }))}
                      />
                    </label>
                    <span className="mono">{formatMoney(lineRefund(l, qtys[l.id] ?? 0))}</span>
                  </div>
                );
              })}
            </section>

            <section className="card">
              {split && (
                <p>
                  Qaytarılacaq <strong>{formatMoney(Math.round(refund * 100) / 100)}</strong> · naxd {formatMoney(split.cash)} · kart {formatMoney(split.card)} · nisyə{' '}
                  {formatMoney(split.credit)}
                </p>
              )}
              {needPin && (
                <label>
                  Menecer PİN-i
                  <input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="off" />
                </label>
              )}
              <button type="button" className="btn-primary" onClick={submit} disabled={busy || picked.length === 0}>
                Qaytarmanı təsdiqlə
              </button>
            </section>
          </>
        )}
        {!card && hasPermission(me, 'sale.return_no_receipt') && <NoReceiptReturn onDone={setDone} />}
      </main>
    </div>
  );
}
